import { vec3 } from 'gl-matrix';
import { Modality } from '~/domain/dicom/modality';
import { DicomImageSlice } from '~/domain/dicom/dicomImageSlice';
import { Series } from '~/domain/dicom/series';
import { Image3D } from '~/domain/patient/image3d';
import { computeSliceAABB } from '~/domain/services/assembleVolume';
import { WorldBoundsMm } from './gridGeometry';
import { OrientedBoxMm } from './orientedBox';
import { sliceStackFrame } from '~/domain/services/sliceStackFrame';

/**
 * What a reformat reads its voxels from: a series that never became an
 * {@link Image3D} (non-uniform spacing, oblique planes, missing slices), or
 * an image already loaded into the patient aggregate.
 */
export type ReformatTarget =
    | { kind: 'rawSeries'; series: Series }
    | { kind: 'loadedImage'; image: Image3D };

/**
 * Why a reformat cannot proceed. {@link reformatBlockedReason} covers the
 * reasons that depend only on the target; `reformatRegionRefusal` (in
 * `reformatEligibility.ts`) covers those that depend on what the user has
 * asked for.
 */
export type ReformatBlockedReason =
    | 'widePixelRange'
    | 'imageStillLoading'
    | 'imageIncomplete'
    | 'mixedGeometry'
    | 'regionOutsideSource'
    | 'tooFewSlices'
    | 'outputTooLarge'
    | 'seriesNumberTaken'
    | 'seriesNumberOutOfRange'
    | 'seriesDescriptionInvalid'
    | 'extensionNeedsMultipleSlices';

/** Modality of whichever target shape is in hand. */
export function reformatTargetModality(target: ReformatTarget): Modality {
    return target.kind === 'rawSeries'
        ? target.series.seriesModule.modality
        : target.image.seriesMetadata.modality;
}

/**
 * The one eligibility predicate for reformatting, or null when the target
 * can be reformatted. The UI mirrors it to disable its control and name the
 * reason; `executeReformat` throws on it. Both reading the same predicate
 * is what stops a blocked target reaching the workflow through an entry
 * point the UI forgot to gate.
 *
 * `widePixelRange` — the reformat pipeline is Int16 end to end: the source
 * read, the resampler and the derived `PixelData`. PET rescales past that
 * range, so its pixel accessors throw rather than wrap values into negative
 * Int16 territory ({@link Image3D.pixelData}, `DicomImageSlice.pixelData`).
 * Blocking here turns that into a stated reason instead of a reformat that
 * starts and then fails. This does NOT mean PET can never be reformatted —
 * it means doing so requires the Float32 path, which this pipeline does not
 * have.
 *
 * `imageStillLoading` / `imageIncomplete` — a progressively loaded image has
 * no whole volume to resample until its slices have arrived. Reading one
 * mid-stream would silently reformat whatever had landed by then, so
 * {@link Image3D.pixelData} throws; naming the reason here turns that into
 * something the user can wait out (still loading) or act on (slices that
 * will never arrive) rather than a reformat that starts and dies. A raw
 * series is never progressive — its slices are parsed before it exists.
 */
export function reformatBlockedReason(
    target: ReformatTarget
): ReformatBlockedReason | null {
    if (target.kind === 'loadedImage') {
        if (target.image.hasWidePixelRange) {
            return 'widePixelRange';
        }
        if (target.image.isIncompleteFinal) {
            return 'imageIncomplete';
        }
        if (!target.image.isComplete) {
            return 'imageStillLoading';
        }
        return null;
    }
    // A raw series has no assembled volume to ask, so gate on the modality
    // that produces the wide range.
    return target.series.seriesModule.modality === Modality.PT
        ? 'widePixelRange'
        : null;
}

/** The slices a target is read from, whichever shape it takes. */
export function reformatSourceSlices(
    target: ReformatTarget
): DicomImageSlice[] {
    return target.kind === 'rawSeries'
        ? target.series.dicomImageSlices
        : target.image.imageSlices;
}

/**
 * The extent the source's VOXELS occupy, in patient mm.
 *
 * `computeSliceAABB` extends each slice by half a pixel along its ROW and
 * COLUMN directions only, so the box is short by half a slice at each end
 * of the STACK. The correction is therefore applied along the slice
 * normal, projected onto each patient axis — not onto Z, which is the
 * stack axis for a transversal acquisition and an in-plane axis for a sagittal
 * or coronal one. Adding it to Z regardless over-extended the box on a
 * non-transversal source, so a region reaching past the real data reported no
 * padding, while that source's true stack axis went uncorrected.
 *
 * The dialog and the reformat itself both measure padding against this, so
 * the proportion the user accepts is the proportion the output records.
 */
export function reformatSourceBoundsMm(target: ReformatTarget): WorldBoundsMm {
    const slices = reformatSourceSlices(target);
    const { worldMin, worldMax } = computeSliceAABB(slices);
    const frame = sliceStackFrame(slices);
    const halfSlice = frame.spacing / 2;
    // Along the direction the stack STEPS in, projected onto each patient
    // axis. Not the slice normal, which is a different direction on a stack
    // whose positions step off it, and not patient Z, which is the stack
    // axis only for a transversal acquisition.
    const halfAlongAxis = [0, 1, 2].map(
        (i) => Math.abs(frame.stepDir[i]) * halfSlice
    );
    return {
        minX: worldMin[0] - halfAlongAxis[0],
        minY: worldMin[1] - halfAlongAxis[1],
        minZ: worldMin[2] - halfAlongAxis[2],
        maxX: worldMax[0] + halfAlongAxis[0],
        maxY: worldMax[1] + halfAlongAxis[1],
        maxZ: worldMax[2] + halfAlongAxis[2],
    };
}

/**
 * The extent the source's VOXELS occupy, as the parallelepiped they
 * actually form.
 *
 * {@link reformatSourceBoundsMm} gives the axis-aligned box around this,
 * which is the same shape only for a transversal source. Padding is measured
 * against this one, so an oblique source's corner wedges count as the
 * padding they are.
 *
 * Built from the first slice's in-plane geometry and the direction the
 * stack steps in — the same two things `assembleVolumeFromSlices` builds
 * the source grid from, so the box and the voxels describe one volume.
 */
export function reformatSourceBoxMm(target: ReformatTarget): OrientedBoxMm {
    const slices = reformatSourceSlices(target);
    if (slices.length === 0) {
        throw new Error('reformatSourceBoxMm: target has no slices');
    }
    const frame = sliceStackFrame(slices);
    const first = frame.ordered[0];

    // ImagePositionPatient is the first voxel's centre; the box starts at
    // its outer corner, half a voxel back along every axis of the frame.
    const origin = vec3.clone(frame.firstPosition);
    vec3.scaleAndAdd(origin, origin, frame.rowDir, -0.5 * frame.dx);
    vec3.scaleAndAdd(origin, origin, frame.colDir, -0.5 * frame.dy);
    vec3.scaleAndAdd(origin, origin, frame.stepDir, -0.5 * frame.spacing);

    return {
        origin,
        edges: [
            vec3.scale(vec3.create(), frame.rowDir, first.cols * frame.dx),
            vec3.scale(vec3.create(), frame.colDir, first.rows * frame.dy),
            vec3.scale(vec3.create(), frame.stepDir, frame.stackLengthMm),
        ],
    };
}
